/**
 * A wrapper around a Wix form that adds expiration tracking and submission management.
 * Intake forms collect information from contacts with time-based validity requirements.
 */
export interface IntakeForm {
  /**
   * Intake form ID. Identical to the [Wix Forms form ID](https://dev.wix.com/docs/api-reference/crm/forms/form-schemas/form-object).
   * @format GUID
   * @readonly
   */
  id?: string | null;
  /**
   * Expiration period in months. If not set, submissions don't expire.
   * @min 1
   * @max 60
   */
  expirationPeriodInMonths?: number | null;
  /**
   * Whether the intake form is archived and disabled from accepting new submissions.
   * @readonly
   */
  archived?: boolean | null;
  /**
   * Intake form name.
   * @minLength 1
   * @maxLength 100
   * @readonly
   */
  name?: string | null;
  /**
   * Date and time the intake form was created in UTC datetime `YYYY-MM-DDThh:mm:ssZ` format.
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * Date and time the intake form was updated in UTC datetime `YYYY-MM-DDThh:mm:ssZ` format.
   * @readonly
   */
  updatedDate?: Date | null;
  /**
   * Revision number, which increments by 1 each time the intake form is updated.
   * To prevent conflicting changes, the current revision must be passed when updating the intake form.
   * @readonly
   */
  revision?: string | null;
}

/** A reference to a Wix Forms field that triggers `PENDING_APPROVAL` when filled. */
export interface PendingTriggerField {
  /**
   * ID of the [Wix Forms field](https://dev.wix.com/docs/api-reference/crm/forms/form-schemas/form-object) that drives the trigger.
   * Must reference a visible checkbox input field. Configuring an unsupported,
   * hidden, or unknown field via `UpdateIntakeFormSettings` is rejected with `UNSUPPORTED_TRIGGER_FIELD`.
   * Stored as the stable field ID (not the translatable label).
   * @format GUID
   */
  formFieldId?: string;
}

/** How an intake form decides which of its submissions require manual approval before becoming valid. */
export enum ApprovalMode {}

/** @enumType */
export type ApprovalModeWithLiterals = ApprovalMode;

export interface QueryIntakeFormsRequest {
  /**
   * Filter object.
   * See @API Query Language
   * for more information.
   *
   * For a detailed list of supported filters, see
   * @Supported Filters.
   */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about the [filter section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-filter-section).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about the [sort section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-sort-section).
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryIntakeFormsResponse {
  /** Retrieved intake forms. */
  intakeForms?: IntakeForm[];
  /** Paging metadata. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in current page. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface UpdateIntakeFormExpirationPeriodRequest {
  /**
   * Intake form ID.
   * @format GUID
   */
  intakeFormId: string | null;
  /**
   * New expiration period in months. If not set, submissions don't expire.
   * @min 1
   * @max 60
   */
  expirationPeriodInMonths?: number | null;
  /**
   * Revision number, which increments by 1 each time the intake form is updated.
   * To prevent conflicting changes, you must specify the current revision when updating the intake form.
   */
  formRevision: string | null;
}

export interface UpdateIntakeFormExpirationPeriodResponse {
  /** Updated intake form. */
  intakeForm?: IntakeForm;
}

export interface UpdateIntakeFormSettingsRequest {
  /**
   * Intake form ID.
   * @format GUID
   */
  intakeFormId?: string;
  /**
   * Revision number, which increments by 1 each time the intake form is updated.
   * To prevent conflicting changes, you must specify the current revision when updating the intake form.
   */
  formRevision?: string;
  /**
   * Base expiration period in months. Omit to leave unchanged; explicit null clears it (submissions don't expire).
   * @min 1
   * @max 60
   */
  expirationPeriodInMonths?: number | null;
  /**
   * Expiration period in months applied only to submissions that fire a trigger field. Has no effect when `pending_trigger_fields` is empty. Omit to leave unchanged; explicit null clears it (triggered submissions fall back to the base period).
   * @min 1
   * @max 60
   */
  triggeredExpirationPeriodInMonths?: number | null;
}

export interface UpdateIntakeFormSettingsResponse {
  /** Updated intake form. */
  intakeForm?: IntakeForm;
}

export interface CreateCustomerSubmissionLinkRequest {
  /**
   * Intake form ID.
   * @format GUID
   */
  intakeFormId: string | null;
  /**
   * [Contact](https://dev.wix.com/docs/api-reference/crm/members-contacts/contacts/contacts/introduction) ID to personalize and pre-fill the submission with the contact's information.
   * @format GUID
   */
  contactId?: string | null;
}

export interface CreateCustomerSubmissionLinkResponse {
  /**
   * Generated link URL for the intake form submission.
   * @minLength 1
   * @maxLength 2048
   */
  intakeFormUrl?: string | null;
  /**
   * Encrypted token containing contact details. Available only if you specified `contactId` in request.
   * @maxLength 2048
   */
  token?: string | null;
}

export interface DeleteIntakeFormRequest {
  /**
   * Intake form ID.
   * @format GUID
   */
  intakeFormId: string | null;
}

export interface DeleteIntakeFormResponse {}

export interface ArchiveIntakeFormRequest {
  /**
   * Intake form ID.
   * @format GUID
   */
  intakeFormId: string | null;
  /**
   * Revision number, which increments by 1 each time the intake form is updated.
   * To prevent conflicting changes, you must specify the current revision when updating the intake form.
   */
  formRevision: string | null;
}

export interface ArchiveIntakeFormResponse {
  /** Archived intake form. */
  intakeForm?: IntakeForm;
}

export interface UnarchiveIntakeFormRequest {
  /**
   * Intake form ID.
   * @format GUID
   */
  intakeFormId: string | null;
  /**
   * Revision number, which increments by 1 each time the intake form is updated.
   * To prevent conflicting changes, you must specify the current revision when updating the intake form.
   */
  formRevision: string | null;
}

export interface UnarchiveIntakeFormResponse {
  /** Unarchived intake form. */
  intakeForm?: IntakeForm;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface Empty {}

export interface DecryptContactDetailsFromTokenRequest {
  /**
   * Encrypted token containing contact details and creation timestamp.
   * Generated by [Create Customer Submission Link](https://dev.wix.com/docs/api-reference/crm/forms/other-services/intake-forms/intake-forms/create-customer-submission-link) when a `contactId` is provided.
   * @minLength 1
   * @maxLength 2048
   */
  token?: string;
}

export interface DecryptContactDetailsFromTokenResponse {
  /** Contact details extracted from the token. */
  contactDetails?: ContactDetails;
}

export interface ContactDetails {
  /**
   * Contact email address. Always present.
   * @format EMAIL
   */
  email?: string;
  /**
   * Contact first name. Present when the contact record has a first name.
   * @maxLength 200
   */
  firstName?: string | null;
  /**
   * Contact last name. Present when the contact record has a last name.
   * @maxLength 200
   */
  lastName?: string | null;
}

/** @docsIgnore */
export type CreateCustomerSubmissionLinkApplicationErrors = {
  code?: 'MISSING_SITE_URL';
  description?: string;
  data?: Record<string, any>;
};
